import React, { useState } from 'react';
import { Alert, Col, Container, Form, FormGroup, Input, Label, Row, Card, CardBody, CardHeader, Badge } from "reactstrap";

// import images
import logo from "../../assets/images/logo-light.png";
import logoDark from "../../assets/images/logo-dark.png";
import { useRecoilValue } from "recoil";
import { themeTypeState } from '../../state/GlobalState';
import { Loader } from '../../custom/components/Loader';
import { searchRaceKitsByIDRequest } from '../../api/controller/RaceKitController';

const RaceKitStatus = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [raceKits, setRaceKits] = useState([]);
    const [idNumber, setIdNumber] = useState("");
    const themeType = useRecoilValue(themeTypeState);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");
        setRaceKits([]);

        try {
            const response = await searchRaceKitsByIDRequest({ id_number: idNumber });
            
            if (response.success) {
                setRaceKits(response.data || []);
                if (response.data && response.data.length > 0) {
                    setSuccess(`Found ${response.data.length} race kit(s) for ID: ${idNumber}`);
                } else {
                    setError("No race kits found for this ID number.");
                }
            } else {
                setError(response.message || "An error occurred while searching.");
            }
        } catch (err) {
            setError("An error occurred while searching. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        if (status === 'PENDING') {
            return <Badge color="secondary" style={{fontSize: '14px', padding: '8px 12px'}}>Pending Collection</Badge>;
        } else {
            return <Badge color="success" style={{fontSize: '14px', padding: '8px 12px'}}>Collected</Badge>;
        }
    };

    return (
        <React.Fragment>
            <Container fluid className="p-0" style={{overflow:'hidden'}}>
                <Loader loading={loading} transparent />
                <div style={{minHeight: '100vh', backgroundColor: 'white'}}>
                    <Container>
                        <div className="p-4" align="center">
                            <div className="text-center mb-4" align="center">
                                <div className="d-flex justify-content-center">
                                    <img
                                        src={themeType === 'dark' ? logo : logoDark}
                                        alt="irunners-logo"
                                        width="200px"
                                        className='logo-light'
                                        style={{justifyContent:'center', aligneItem:'center'}} 
                                    />
                                </div>
                            </div>
                            
                            <Row className="justify-content-center">
                                <Col md={6} lg={4}>
                                    <Card style={{border: '1px solid'}}>
                                        <CardHeader>
                                            <h4 className="text-center mb-0">Race Kit Status</h4>
                                        </CardHeader>
                                        <CardBody>
                                            <p className="text-muted text-center mb-4">
                                                Enter your ID number to check your race kit status
                                            </p>

                                            {error && (
                                                <Alert color="danger" className="mb-4">
                                                    {error}
                                                </Alert>
                                            )}

                                            {success && (
                                                <Alert color="success" className="mb-4">
                                                    {success}
                                                </Alert>
                                            )}

                                            <Form onSubmit={handleSubmit}>
                                                <FormGroup>
                                                    <Label for="idNumber">ID Number</Label>
                                                    <Input
                                                        type="text"
                                                        name="idNumber"
                                                        id="idNumber"
                                                        placeholder="Enter your ID number"
                                                        value={idNumber}
                                                        onChange={(e) => setIdNumber(e.target.value)}
                                                        required
                                                    />
                                                </FormGroup>

                                                <div className="mt-4" align="center">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary btn-block waves-effect waves-light"
                                                        disabled={loading || !idNumber.trim()}
                                                    >
                                                        {loading ? 'Searching...' : 'Search'}
                                                    </button>
                                                </div>
                                            </Form>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {raceKits.length > 0 && (
                                <Row className="mt-4">
                                    <Col>
                                        <h5 className="mb-3" align="center">Your Race Kits</h5>
                                        <Row className="justify-content-center">
                                            {raceKits.map((kit, index) => (
                                                <Col 
                                                    key={index} 
                                                    xs={12} 
                                                    md={raceKits.length === 1 ? 6 : 6} 
                                                    lg={raceKits.length === 1 ? 4 : 6} 
                                                    className="mb-3"
                                                >
                                                    <Card className="h-100" style={{border: '1px solid', boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
                                                        <CardBody className="text-center">
                                                            {/* Prominent Bib Number */}
                                                            <div className="mb-3">
                                                                <h1 className="display-4 font-weight-bold text-primary">
                                                                    {kit.bib_number || 'N/A'}
                                                                </h1>
                                                                <small className="text-muted">Bib Number</small>
                                                            </div>
                                                            
                                                            <hr />
                                                            
                                                            {/* Participant Info */}
                                                            <div className="mb-3">
                                                                <h5 className="mb-1">{kit.participant_name || 'Unknown'}</h5>
                                                                <p className="text-muted mb-0">{kit.race || 'Unknown Race'}</p>
                                                            </div>
                                                            
                                                            {/* Status */}
                                                            <div className="mb-3">
                                                                {getStatusBadge(kit.status)}
                                                            </div>
                                                            
                                                            {/* Receiver Info */}
                                                            {kit.receiver_name && (
                                                                <div className="mb-3">
                                                                    <strong>Received by:</strong><br />
                                                                    <span className="text-info">{kit.receiver_name}</span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Tee Information */}
                                                            {kit.tee_info && kit.tee_info.length > 0 && (
                                                                <div className="mb-3">
                                                                    <strong><i className="mdi mdi-tshirt-crew-outline mr-1"></i></strong><br />
                                                                    {kit.tee_info.map((tee, teeIndex) => (
                                                                        <span key={teeIndex} className="mr-1 mb-1">
                                                                            {tee}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </Container>
                </div>
            </Container>
        </React.Fragment>
    );
};

export default RaceKitStatus; 